// The signed-out screen, and the full-page messages (account deactivated, something went wrong).

import { useState } from "react";
import logo from "../assets/gum-logo.svg";
import { useStore } from "../store/StoreProvider";
import type { User } from "../types";
import { Avatar } from "../ui/bits";
import { Icon } from "../ui/Icon";
import { useToast } from "../ui/Toasts";

export function Splash() {
  return (
    <div className="splash" role="status">
      Loading…
    </div>
  );
}

export function MessagePage({
  title,
  children,
  onSignOut,
  onRetry,
  onExitDemo,
}: {
  title: string;
  children: string;
  onSignOut?: () => void;
  onRetry?: () => void;
  onExitDemo?: () => void;
}) {
  return (
    <div className="login login-single">
      <div className="card login-card">
        <img src={logo} alt="GUM" width={72} height={34} />
        <h1 className="h2">{title}</h1>
        <p>{children}</p>
        {onRetry && (
          <button type="button" className="btn btn-primary" onClick={onRetry}>
            Retry
          </button>
        )}
        {onSignOut && (
          <button type="button" className="btn btn-secondary" onClick={onSignOut}>
            Sign out
          </button>
        )}
        {onExitDemo && (
          <button type="button" className="btn btn-secondary" onClick={onExitDemo}>
            Exit demo
          </button>
        )}
      </div>
    </div>
  );
}

export function Login({ onEnterDemo, onExitDemo }: { onEnterDemo?: () => void; onExitDemo?: () => void }) {
  const store = useStore();
  return (
    <div className="login">
      <section className="login-hero">
        <img src={logo} alt="GUM" width={120} height={56} />
        <h1>Playbrush Holiday</h1>
        <p>Book time off, see who is away and keep your holiday balance in one place.</p>
        <ul className="login-points">
          <li>
            <Icon name="calendar" />A shared team calendar
          </li>
          <li>
            <Icon name="check-circle" />
            Quick approvals from your team lead
          </li>
          <li>
            <Icon name="sun" />
            Live holiday balance, UK bank holidays built in
          </li>
        </ul>
      </section>
      <section className="login-panel">
        <div className="card login-card">
          {store.mode === "demo" ? <DemoPicker /> : <MicrosoftSignIn />}
          {store.mode === "api" && onEnterDemo && (
            <button type="button" className="btn btn-secondary" onClick={onEnterDemo}>
              Demo
            </button>
          )}
          {store.mode === "demo" && onExitDemo && (
            <button type="button" className="btn btn-secondary" onClick={onExitDemo}>
              Exit demo
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

function DemoPicker() {
  const store = useStore();
  if (store.mode !== "demo") return null;
  const { users } = store.directory();
  const groups: [string, User[]][] = [
    ["HR", users.filter((u) => u.role === "hr")],
    ["Product managers", users.filter((u) => u.role !== "hr" && u.isProductManager)],
    ["Team leads", users.filter((u) => u.role !== "hr" && u.isTeamLead)],
    ["Employees", users.filter((u) => u.role !== "hr" && !u.isTeamLead && !u.isProductManager)],
  ];
  return (
    <>
      <h2>Try the demo</h2>
      <p className="muted">Pick someone to sign in as. Nothing here is real data.</p>
      {groups
        .filter(([, list]) => list.length)
        .map(([label, list]) => (
          <section key={label}>
            <h3 className="subhead">{label}</h3>
            <div className="persona-grid">
              {list.map((u) => (
                <button key={u.id} type="button" className="persona" onClick={() => void store.signInAs(u.id)}>
                  <Avatar name={u.name} photoUrl={u.photoUrl} />
                  {u.name}
                  {!u.active && <span className="muted"> (deactivated)</span>}
                </button>
              ))}
            </div>
          </section>
        ))}
    </>
  );
}

function MicrosoftSignIn() {
  const store = useStore();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const signIn = async () => {
    if (store.mode !== "api") return;
    setBusy(true);
    try {
      await store.signIn();
    } catch (e) {
      console.error(e);
      // Closing the sign-in window isn't an error worth shouting about.
      if ((e as { errorCode?: string }).errorCode !== "user_cancelled") {
        toast((e instanceof Error && e.message) || "Sign-in failed. Please try again.", "error");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h2>Sign in</h2>
      <p className="muted">Use your company Microsoft 365 account.</p>
      <button type="button" className="btn btn-ms" onClick={signIn} disabled={busy}>
        <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true">
          <rect x="1" y="1" width="9" height="9" fill="#f25022" />
          <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
          <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
          <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
        </svg>
        Sign in with Microsoft
      </button>
    </>
  );
}
